'use client'

import { useEffect, useRef } from 'react'

/**
 * Header chrome over the hero. Renders the hero's top sentinel and, while it
 * is mounted, publishes two facts to the document root:
 *
 * - `--mist-header-height`: the masthead's measured height, so the hero can
 *   slide under it by exactly that much. globals.css carries per-breakpoint
 *   defaults for no-JS and first paint; the measurement only corrects them.
 * - `data-hero-chrome="transparent"`: set while the sentinel is on screen and
 *   cleared otherwise. SiteHeader.module.css reads it to drop the bar's
 *   background, hairline and blur, so the masthead sits as type over the hero
 *   and returns to its solid near-black bar once the hero scrolls away.
 *   Without JavaScript the attribute never exists and the bar stays solid.
 *
 * The sentinel is a short band (globals.css `.hero__sentinel`) at the hero's
 * very top, so the bar is transparent only while the page rests at the top
 * and turns solid the moment the visitor scrolls past that band — before the
 * object, let alone the type, could slide under bare nav — and a hash landing
 * on any later chapter finds it solid already. Mounted by the hero, so
 * leaving the home route restores the default chrome everywhere else.
 */

export const HERO_CHROME_ATTRIBUTE = 'data-hero-chrome'
export const HEADER_HEIGHT_PROPERTY = '--mist-header-height'

export default function HeaderChrome() {
  const sentinelRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const root = document.documentElement
    const header = document.querySelector<HTMLElement>('[data-site-header]')
    const sentinel = sentinelRef.current

    let resize: ResizeObserver | undefined
    if (header && typeof ResizeObserver !== 'undefined') {
      const publish = () => {
        const height = Math.round(header.getBoundingClientRect().height)
        if (height > 0) root.style.setProperty(HEADER_HEIGHT_PROPERTY, `${height}px`)
      }
      publish()
      resize = new ResizeObserver(publish)
      resize.observe(header)
    }

    let intersection: IntersectionObserver | undefined
    if (sentinel && typeof IntersectionObserver !== 'undefined') {
      intersection = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) root.setAttribute(HERO_CHROME_ATTRIBUTE, 'transparent')
        else root.removeAttribute(HERO_CHROME_ATTRIBUTE)
      })
      intersection.observe(sentinel)
    }

    return () => {
      resize?.disconnect()
      intersection?.disconnect()
      root.removeAttribute(HERO_CHROME_ATTRIBUTE)
      root.style.removeProperty(HEADER_HEIGHT_PROPERTY)
    }
  }, [])

  return <span ref={sentinelRef} className="hero__sentinel" data-hero-sentinel="" aria-hidden="true" />
}
